import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useToast } from '@/shared/hooks/useToast';
import { ChecklistCard } from './ChecklistCard';
import { AddChecklistInline } from './AddChecklistInline';
import { computeMoveOrder } from '../utils/computeMoveOrder';
import { useTaskChecklists } from '../hooks/useTaskChecklists';
import {
  useCreateChecklist,
  useDeleteChecklist,
  useUpdateChecklist,
} from '../hooks/useChecklistMutations';
import {
  useAddChecklistItem,
  useConvertItemToSubtask,
  useConvertItemToTask,
  useDeleteChecklistItem,
  useMoveChecklistItem,
  useUpdateChecklistItem,
} from '../hooks/useChecklistItemMutations';
import type { Checklist } from '../types';

interface ChecklistSectionProps {
  taskId: string;
  /**
   * Whether checklist items may be converted into subtasks. Pass `false` for
   * subtask detail views: subtasks cannot have subtasks, so the convert action
   * is hidden there (typically `!task.parentId` from the task detail).
   */
  canConvertToSubtask: boolean;
}

/**
 * Renders all checklists of a task inside the task detail. Owns checklist/item
 * mutations and the cross-checklist drag-and-drop monitor (item reorder + move).
 */

interface DragData {
  itemId?: string;
  checklistId?: string;
  order?: number;
}

export function ChecklistSection({ taskId, canConvertToSubtask }: ChecklistSectionProps) {
  const { t } = useTranslation('checklists');
  const { toast } = useToast();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const { data: checklists, isLoading } = useTaskChecklists(taskId);

  const createChecklist = useCreateChecklist(taskId);
  const updateChecklist = useUpdateChecklist(taskId);
  const deleteChecklist = useDeleteChecklist(taskId);
  const addItem = useAddChecklistItem(taskId);
  const updateItem = useUpdateChecklistItem(taskId);
  const deleteItem = useDeleteChecklistItem(taskId);
  const moveItem = useMoveChecklistItem(taskId);
  const convertToTask = useConvertItemToTask(taskId);
  const convertToSubtask = useConvertItemToSubtask(taskId);

  useEffect(() => {
    if (!checklists) return;
    return monitorForElements({
      onDrop({ source, location }) {
        const targets = location.current.dropTargets;
        if (targets.length === 0) return;

        const src = source.data as DragData;
        if (!src.itemId || !src.checklistId) return;

        const inner = targets[0].data as DragData;
        const targetChecklistId = inner.checklistId;
        if (!targetChecklistId) return;
        if (inner.itemId === src.itemId) return;

        const targetChecklist = checklists.find((c) => c.id === targetChecklistId);
        if (!targetChecklist) return;

        const order = computeMoveOrder(targetChecklist.items, src.itemId, inner.itemId);

        moveItem.mutate({
          itemId: src.itemId,
          data: { targetChecklistId, order },
        });
      },
    });
  }, [checklists, moveItem]);

  async function handleDeleteChecklist(checklist: Checklist) {
    const result = await confirm({
      title: t('confirmDelete.title'),
      description: t('confirmDelete.description', { name: checklist.name }),
      confirmLabel: t('confirmDelete.confirm'),
      cancelLabel: t('confirmDelete.cancel'),
      variant: 'destructive',
    });
    if (result.confirmed) deleteChecklist.mutate(checklist.id);
  }

  async function handleConvertToTask(itemId: string) {
    try {
      await convertToTask.mutateAsync(itemId);
      toast.success({ title: t('toasts.convertedToTask') });
    } catch {
      // global handler shows error toast
    }
  }

  async function handleConvertToSubtask(itemId: string) {
    try {
      await convertToSubtask.mutateAsync(itemId);
      toast.success({ title: t('toasts.convertedToSubtask') });
    } catch {
      // global handler shows error toast
    }
  }

  return (
    <section data-testid="checklists-section" aria-label={t('section.title')}>
      {isLoading && (
        <div className="flex flex-col gap-2" data-testid="checklists-loading">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {!isLoading && checklists && checklists.length === 0 && (
        <p className="py-2 text-xs text-muted-foreground" data-testid="checklists-empty">
          {t('section.empty')}
        </p>
      )}

      {!isLoading && checklists && checklists.length > 0 && (
        <div className="flex flex-col gap-3">
          {checklists
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((checklist) => (
              <ChecklistCard
                key={checklist.id}
                checklist={checklist}
                canConvertToSubtask={canConvertToSubtask}
                onAddItem={(checklistId, title) => addItem.mutate({ checklistId, data: { title } })}
                onRenameChecklist={(checklistId, name) =>
                  updateChecklist.mutate({ checklistId, data: { name } })
                }
                onDeleteChecklist={handleDeleteChecklist}
                onToggleItem={(itemId, isChecked) =>
                  updateItem.mutate({ itemId, data: { isChecked } })
                }
                onRenameItem={(itemId, title) => updateItem.mutate({ itemId, data: { title } })}
                onDeleteItem={(itemId) => deleteItem.mutate(itemId)}
                onConvertItemToTask={handleConvertToTask}
                onConvertItemToSubtask={handleConvertToSubtask}
              />
            ))}
        </div>
      )}

      <div className="mt-2">
        <AddChecklistInline onCreate={(name) => createChecklist.mutate({ name })} />
      </div>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(v) => !v && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
          data-testid="checklists-delete-confirm"
        />
      )}
    </section>
  );
}
